import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAPI } from "../../service/Encrtyption";
import { useTranslation } from "react-i18next";
import { useParamarshDetails } from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactGA from "react-ga4";
import Header from "../../layout/Header";
import { Loader } from "../../loader";
import { amountTransferData } from "../../auth/redux/store";
import {
  getTransferErrorMessage,
  isTransferSuccess,
  parsePaymentApiResponse,
} from "../../../utils/paymentResponse";

const BANK_PAYMENT_DRAFT_KEY = "strongram_bank_payment_draft_v1";

const maskAccountNumber = (acct) => {
  const digits = String(acct ?? "").replace(/\s/g, "");
  if (!digits) return "";
  const tail = digits.slice(-4);
  if (digits.length <= 4) return tail;
  return `xxxxxxx${tail}`;
};


const styles = `
 .loader-overlay {
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999999999999;
}

.loader-spinner {
  border: 10px solid #5F6368;
  border-top: 10px solid var(--secondary-color);
  border-radius: 50%;
  width: 140px;
  height: 140px;
  animation: spin 1s linear infinite;
}

.loader-img {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99999
}
`;

export const ConfirmBankDetails = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const browserType = sessionStorage.getItem("browserName") || "web"; 
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const claimValue = useSelector((state) => state.claimValue);
  const reclaimDataObj = useSelector((state) => state.reclaimDataObj);
  const amount = useSelector((state) => state.amount);

  const [fetchData, rewardsData, , , cashRewardsData] =
    useParamarshDetails();

  const [draft, setDraft] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const mobileDisplay = useMemo(
    () =>
      user?.mobileNumber ||
      sessionStorage.getItem("mbn") ||
      draft?.phoneNumber ||
      "",
    [user?.mobileNumber, draft?.phoneNumber]
  );

  const farmerDisplayName = useMemo(() => {
    const fromProfile = [
      user?.firstName,
      user?.middleName,
      user?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (fromProfile) return fromProfile;
    return draft?.accountName || "";
  }, [draft?.accountName, user?.firstName, user?.lastName, user?.middleName]);

  const displayAmount = useMemo(() => {
    if (draft?.cashRewards != null && draft.cashRewards !== "") {
      return draft.cashRewards;
    }
    return claimValue
      ? amount
      : reclaimDataObj?.amount != null
        ? reclaimDataObj.amount
        : amount;
  }, [amount, claimValue, draft?.cashRewards, reclaimDataObj?.amount]);

  useEffect(() => {
    if (claimValue) fetchData();
  }, [claimValue, fetchData]);

  useEffect(() => {
    const raw = sessionStorage.getItem(BANK_PAYMENT_DRAFT_KEY);
    if (!raw) {
      navigate(`/${lang}/bankdetails`, { replace: true });
      return;
    }
    try {
      setDraft(JSON.parse(raw));
    } catch {
      navigate(`/${lang}/bankdetails`, { replace: true });
    }
  }, [lang, navigate]);

  const buildApiPayload = useCallback(
    (base) => {
      const next = { ...base };
      if (claimValue) {
        next.cashRewards =
          cashRewardsData !== undefined && cashRewardsData !== ""
            ? cashRewardsData
            : base.cashRewards;
        next.claimIDs =
          Array.isArray(rewardsData) && rewardsData.length > 0
            ? rewardsData
            : base.claimIDs || [];
      } else {
        next.cashRewards = reclaimDataObj?.amount ?? base.cashRewards;
        next.claimIDs = [];
        next.cashFreeTransactionId =
          reclaimDataObj?.cashFreeTransactionId ?? base.cashFreeTransactionId;
      }
      const phone =
        user?.mobileNumber ||
        sessionStorage.getItem("mbn") ||
        base.phoneNumber;
      next.phoneNumber = phone;
      return next;
    },
    [
      cashRewardsData,
      claimValue,
      reclaimDataObj?.amount,
      reclaimDataObj?.cashFreeTransactionId,
      rewardsData,
      user?.mobileNumber,
    ]
  );

  const goEdit = useCallback(() => {
    navigate(`/${lang}/bankdetails`);
  }, [navigate, lang]);

  const submitPayment = useCallback(async () => {
    if (!draft) return;

    ReactGA.event({
      category: "User Bank Details",
      action: "CLAIM_BANK_CONFIRM",
    });

    const payload = buildApiPayload(draft);
    const apiMobile =
      user?.mobileNumber || sessionStorage.getItem("mbn") || "";

    const missingClaimIds =
      !!claimValue &&
      (!Array.isArray(payload.claimIDs) || payload.claimIDs.length === 0);
    if (
      !payload.cashRewards ||
      !payload.accountNumber ||
      !payload.ifscCode ||
      missingClaimIds
    ) {
      setErrorMessage(t("sessionExpired"));
      setTimeout(() => navigate(`/${lang}/home`), 2000);
      return;
    }

    setErrorMessage("");
    setProcessingPayment(true);

    console.log("Submitting payment with payload:" + JSON.stringify(payload));

    try {
      const responseData = await fetchAPI(
        process.env.REACT_APP_API_URL,
        payload,
        {
          mobileNumber: apiMobile,
          appLangCode: lang || i18n.language,
          customerId:
            user?.customerId || sessionStorage.getItem("cid") || "",
          deviceType: "web",
          browserType: browserType,
        }
      );
      const jsonResponseData = parsePaymentApiResponse(responseData);

      sessionStorage.removeItem(BANK_PAYMENT_DRAFT_KEY);

      if (isTransferSuccess(jsonResponseData)) {
        dispatch(amountTransferData(jsonResponseData));
        sessionStorage.removeItem(BANK_PAYMENT_DRAFT_KEY);
        navigate(`/${lang}/transferSucces`);
      } else {
        dispatch(amountTransferData(jsonResponseData));
        const msg = getTransferErrorMessage(jsonResponseData);
        setErrorMessage(msg);
      }
    } catch (error) {
      const msg =
        typeof error?.message === "string" ? error.message : String(error);
      if (
        msg.includes("JSON") ||
        msg.includes("Empty response") ||
        msg.includes("decrypt") ||
        msg.includes("Invalid response")
      ) {
        navigate(`/${lang}/transferFailed`);
        // setErrorMessage(
        //   t("Something_went_wrong") ||
        //     "Payment could not be processed. Please try again."
        // );
      } else {
        setErrorMessage(msg);
      }
    } finally {
      setProcessingPayment(false);
    }
  }, [
    buildApiPayload,
    claimValue,
    dispatch,
    draft,
    lang,
    navigate,
    t,
    user?.mobileNumber,
    user?.customerId,
    lang,
    i18n.language,
  ]);

  if (!draft) {
    return (
      <>
        <Header />
        <Loader />
      </>
    );
  }

  return (
    <>
      <Header />
      <style>{styles}</style>
      <div className="main">
        <div className="container p-0">
          {processingPayment && (
            <Loader />
            // <div
            //   className="payment-processing-overlay"
            //   role="status"
            //   aria-live="polite"
            // >
            //   <div className="payment-processing-spinner" />
            //   <p className="payment-processing-title mb-0">
            //     {t("confirmBankDetails.paymentLoadingPrimary")}
            //   </p>
            //   <p className="payment-processing-subtitle mb-0">
            //     {t("confirmBankDetails.paymentLoadingSecondary")}
            //   </p>
            // </div>
          )}

          <div className="card border-0 mt-5">
            <div className="card-body px-3 pt-0 confirmBankDetailsBody">
              <h3 className="text-center text-secondaryColor font-extrabold">
                ₹ {displayAmount}
              </h3>

              <div className="py-3 px-3 mt-5 bankDetailsWrapper">
                <div>
                  <p className="mb-0">{t("confirmBankDetails.farmerNameLabel")}</p>
                  <p className="mb-0 infoValue">{farmerDisplayName}</p>
                </div>

                <div>
                  <p className="mb-0">{t("confirmBankDetails.mobileLabel")}</p>
                  <p className="mb-0 infoValue">{mobileDisplay}</p>
                </div>

                <div>
                  <p className="mb-0">
                    {t("confirmBankDetails.paymentMethodLabel")}
                  </p>
                  <p className="mb-0 infoValue">
                    {t("confirmBankDetails.bankAccount")}
                  </p>
                </div>

                <div>
                  <p className="mb-0">
                    {t("confirmBankDetails.accountNumberLabel")}
                  </p>
                  <p className="infoValue mb-0">
                    {maskAccountNumber(draft.accountNumber)}
                  </p>
                </div>

                <button
                  type="button"
                  className="text-right editText bg-transparent border-0 p-0 align-self-end"
                  onClick={goEdit}
                >
                  {t("confirmBankDetails.edit")}
                </button>
              </div>

              {errorMessage && (
                <div
                  className="alert alert-warning alert-dismissible fade show mt-3"
                  role="alert"
                >
                  {errorMessage}
                  <button
                    type="button"
                    className="close"
                    onClick={() => setErrorMessage("")}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="confirmBankDetailsFooter">
            <button
              type="button"
              className="btn btn-secondary btnshadow w-100 py-3 center-textbtn text-white text-uppercase"
              onClick={submitPayment}
              disabled={processingPayment}
            >
              {t("confirmBankDetails.confirmProceed")}
            </button>
            <button
              type="button"
              className="btn btn-secondary btnshadow w-100 py-3 center-textbtn text-white text-uppercase mt-3"
              onClick={goEdit}
              disabled={processingPayment}
            >
              {t("confirmBankDetails.edit")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
